// ============================================
// ReviewPage — UKA-X01 D5
//
// The shell Dr. C lands on after magic-link login.
// Mobile-first two-pane layout: left = list + filters, right = ReviewCard.
// Mobile: section list full-screen; selecting a section opens overlay with ReviewCard.
// Desktop (≥768px): persistent two-pane split.
//
// Auth: PortalAuthGuard wraps entire output (requiredRole='REVIEWER').
//   Role hierarchy REVIEWER < OPERATOR < ADMIN — all three pass.
//
// Design: DM Sans body, Instrument Serif title only, no framer-motion.
// Palette: BG #FAFAFA, Cards #FFFFFF, Text #18181B, Rose #E11D48.
// Min 16px font, WCAG AA contrast, 48px touch targets.
// ============================================

import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { PortalAuthGuard } from '../lib/authGuard';
import { useReviewContent } from '../hooks/useReviewContent';
import { ProgressBar } from '../components/ProgressBar';
import { SectionList } from '../components/SectionList';
import { ReviewCard } from '../components/ReviewCard';
import { OfflineIndicator } from '../components/OfflineIndicator';
import type { ApproveAllItem } from '../components/ApproveAllButton';
import type { ContentStatus, ContentType } from '../../data/portal/schema';

// ── Constants ─────────────────────────────────────────────────────────────────

const TENANT_ID = 'hemologix';

const TABS = [
  'All',
  'Methodology',
  'Biomarker Ranges',
  'Formulary Supplement',
  'Formulary Rx',
  'Formulary Longevity',
  'Evidence Briefs',
  'Gap Questions',
  'Attestation',
] as const;

type TabLabel = typeof TABS[number];

// Maps tab label → ContentType for filtering
const TAB_TO_TYPE: Record<Exclude<TabLabel, 'All'>, ContentType> = {
  'Methodology':          'methodology',
  'Biomarker Ranges':     'biomarker_range',
  'Formulary Supplement': 'formulary_supplement',
  'Formulary Rx':         'formulary_rx',
  'Formulary Longevity':  'formulary_longevity',
  'Evidence Briefs':      'evidence_brief',
  'Gap Questions':        'gap_question',
  'Attestation':          'attestation',
};

// "Reviewed" = any terminal state (non-IN_REVIEW, non-STAGED, non-BLOCKED)
const REVIEWED_STATUSES: ContentStatus[] = [
  'APPROVED',
  'APPROVED_WITH_CONDITIONS',
  'REJECTED',
  'CHANGE_REQUESTED',
  'WITHDRAWN',
];

// ── Session timer helper ──────────────────────────────────────────────────────

function formatSessionAge(minutes: number): string {
  if (minutes === 0) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h} hr ${m} min ago` : `${h} hr ago`;
}

// ── ReviewPageInner (rendered after auth passes) ──────────────────────────────

function ReviewPageInner() {
  const { sections, loading, error } = useReviewContent();
  const [activeTab, setActiveTab] = useState<TabLabel>('All');
  const [selectedContentId, setSelectedContentId] = useState<string | null>(null);

  // Session fatigue: track session start time, update display each minute
  const sessionStart = useRef(Date.now());
  const [sessionMinutes, setSessionMinutes] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSessionMinutes(Math.floor((Date.now() - sessionStart.current) / 60000));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // ── Derived data ────────────────────────────────────────────────────────────

  const filteredSections = useMemo(() => {
    if (activeTab === 'All') return sections;
    const type = TAB_TO_TYPE[activeTab as Exclude<TabLabel, 'All'>];
    return sections.filter((s) => s.type === type);
  }, [sections, activeTab]);

  const totalReviewed = useMemo(
    () => sections.filter((s) => REVIEWED_STATUSES.includes(s.status)).length,
    [sections],
  );

  const byCategory = useMemo(() => {
    const result: Record<string, { total: number; reviewed: number }> = {};
    for (const [label, type] of Object.entries(TAB_TO_TYPE)) {
      const cat = sections.filter((s) => s.type === type);
      result[label] = {
        total: cat.length,
        reviewed: cat.filter((s) => REVIEWED_STATUSES.includes(s.status)).length,
      };
    }
    return result;
  }, [sections]);

  // Per-tab section count (for tab badges)
  const tabCount = (tab: TabLabel): number => {
    if (tab === 'All') return sections.length;
    const type = TAB_TO_TYPE[tab as Exclude<TabLabel, 'All'>];
    return sections.filter((s) => s.type === type).length;
  };

  const selectedItem = useMemo(
    () => sections.find((s) => s.id === selectedContentId) ?? null,
    [sections, selectedContentId],
  );

  // Group items for ApproveAllButton — all formulary_supplement sections in the same category
  const groupItems = useMemo((): ApproveAllItem[] => {
    if (!selectedItem || selectedItem.type !== 'formulary_supplement') return [];
    return sections
      .filter((s) => s.type === 'formulary_supplement' && s.category === selectedItem.category)
      .map((s) => ({ contentId: s.id, item: s }));
  }, [sections, selectedItem]);

  // Next unreviewed item after the currently selected one
  const nextItem = useMemo(() => {
    if (!selectedItem) return null;
    const currentIndex = filteredSections.findIndex((s) => s.id === selectedItem.id);
    const candidates = [
      ...filteredSections.slice(currentIndex + 1),
      ...filteredSections.slice(0, currentIndex),
    ];
    return candidates.find((s) => !REVIEWED_STATUSES.includes(s.status)) ?? null;
  }, [filteredSections, selectedItem]);

  const advanceToNext = useCallback((id: string) => {
    const currentIndex = filteredSections.findIndex((s) => s.id === id);
    const candidates = [
      ...filteredSections.slice(currentIndex + 1),
      ...filteredSections.slice(0, currentIndex),
    ];
    const next = candidates.find((s) => !REVIEWED_STATUSES.includes(s.status));
    setSelectedContentId(next?.id ?? null);
  }, [filteredSections]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#FAFAFA',
        fontFamily: 'DM Sans, system-ui, sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Header ── */}
      <header
        style={{
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E4E4E7',
          padding: '0 20px',
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 40,
        }}
      >
        <h1
          style={{
            fontFamily: 'Instrument Serif, Georgia, serif',
            fontSize: '20px',
            fontWeight: 400,
            color: '#18181B',
            margin: 0,
          }}
        >
          Clinical Review Portal
        </h1>
        <span
          style={{ fontSize: '13px', color: '#A1A1AA' }}
          aria-label="Session start time"
        >
          Session started {formatSessionAge(sessionMinutes)}
        </span>
      </header>

      {/* ── Progress bar ── */}
      <div style={{ padding: '16px 20px 0' }}>
        {loading ? (
          <div
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E4E4E7',
              borderRadius: '4px',
              padding: '16px 20px',
              fontSize: '16px',
              color: '#A1A1AA',
            }}
          >
            Loading sections…
          </div>
        ) : error ? (
          <div
            style={{
              backgroundColor: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: '4px',
              padding: '12px 16px',
              fontSize: '16px',
              color: '#B91C1C',
            }}
            role="alert"
          >
            Failed to load review content: {error}
          </div>
        ) : (
          <ProgressBar
            total={sections.length}
            reviewed={totalReviewed}
            byCategory={byCategory}
          />
        )}
      </div>

      {/* ── Category tabs ── */}
      <div
        style={{
          padding: '12px 20px 0',
          overflowX: 'auto',
          display: 'flex',
          gap: '6px',
          // Prevent scroll bar flashing on content load
          scrollbarWidth: 'none',
        }}
        role="tablist"
        aria-label="Filter sections by category"
      >
        {TABS.map((tab) => {
          const isActive = tab === activeTab;
          const count = tabCount(tab);
          return (
            <button
              key={tab}
              role="tab"
              aria-selected={isActive}
              onClick={() => {
                setActiveTab(tab);
                setSelectedContentId(null);
              }}
              style={{
                whiteSpace: 'nowrap',
                padding: '7px 14px',
                borderRadius: '4px',
                border: isActive ? '1px solid #E11D48' : '1px solid #E4E4E7',
                backgroundColor: isActive ? '#FFF1F2' : '#FFFFFF',
                color: isActive ? '#E11D48' : '#52525B',
                fontSize: '14px',
                fontWeight: isActive ? 600 : 400,
                cursor: 'pointer',
                fontFamily: 'DM Sans, system-ui, sans-serif',
                transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
                minHeight: '36px',
              }}
            >
              {tab}
              {count > 0 && (
                <span
                  style={{
                    marginLeft: '6px',
                    fontSize: '12px',
                    backgroundColor: isActive ? '#E11D48' : '#F4F4F5',
                    color: isActive ? '#FFFFFF' : '#71717A',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    fontWeight: 600,
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Main content area: two-pane on desktop, list-only on mobile ── */}
      <div
        style={{
          flex: 1,
          padding: '12px 20px 20px',
          display: 'flex',
          gap: '16px',
          minHeight: 0,
        }}
      >
        {/* Left panel: section list */}
        <div
          style={{
            flex: '0 0 380px',
            maxWidth: '100%',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E4E4E7',
            borderRadius: '4px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
          // On mobile, take full width (no right panel visible)
          className="w-full md:w-auto md:flex-none"
        >
          {/* List header */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #F4F4F5',
              fontSize: '13px',
              color: '#71717A',
              fontWeight: 500,
            }}
          >
            {filteredSections.length} section{filteredSections.length !== 1 ? 's' : ''}
            {activeTab !== 'All' ? ` · ${activeTab}` : ''}
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <SectionList
              sections={filteredSections}
              selectedId={selectedContentId}
              onSelect={setSelectedContentId}
            />
          </div>
        </div>

        {/* Right panel: ReviewCard — desktop only (hidden on mobile via CSS class) */}
        <div
          className="hidden md:flex"
          style={{
            flex: 1,
            backgroundColor: '#FFFFFF',
            border: '1px solid #E4E4E7',
            borderRadius: '4px',
            overflow: 'hidden',
            flexDirection: 'column',
          }}
        >
          {selectedItem ? (
            <div style={{ overflowY: 'auto', height: '100%' }}>
              <ReviewCard
                key={selectedItem.id}
                contentId={selectedItem.id}
                item={selectedItem}
                tenantId={TENANT_ID}
                groupItems={groupItems}
                nextItemTitle={nextItem?.title ?? null}
                onVerdictSubmitted={(id, _action) => advanceToNext(id)}
              />
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#A1A1AA',
                fontSize: '16px',
                flexDirection: 'column',
                gap: '8px',
                padding: '40px',
                textAlign: 'center',
              }}
            >
              <span style={{ fontSize: '32px', lineHeight: 1 }}>←</span>
              <span>Select a section to begin review</span>
              <span style={{ fontSize: '13px' }}>
                {sections.length - totalReviewed} section
                {sections.length - totalReviewed !== 1 ? 's' : ''} remaining
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Offline sync indicator (fixed bottom bar, z-index 60) ── */}
      <OfflineIndicator />

      {/* ── Mobile overlay: full-screen ReviewCard when section selected ── */}
      {selectedItem && (
        <div
          className="md:hidden"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            backgroundColor: '#FAFAFA',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
          role="dialog"
          aria-modal="true"
          aria-label={`Reviewing: ${selectedItem.title}`}
        >
          {/* Mobile overlay header */}
          <div
            style={{
              position: 'sticky',
              top: 0,
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid #E4E4E7',
              padding: '0 16px',
              height: '56px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              zIndex: 51,
            }}
          >
            <button
              onClick={() => setSelectedContentId(null)}
              style={{
                background: 'none',
                border: '1px solid #E4E4E7',
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '14px',
                color: '#18181B',
                cursor: 'pointer',
                fontFamily: 'DM Sans, system-ui, sans-serif',
                minHeight: '36px',
                minWidth: '60px',
              }}
              aria-label="Back to section list"
            >
              ← Back
            </button>
            <span
              style={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#18181B',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedItem.title}
            </span>
          </div>

          {/* ReviewCard in overlay */}
          <div style={{ flex: 1 }}>
            <ReviewCard
              key={selectedItem.id}
              contentId={selectedItem.id}
              item={selectedItem}
              tenantId={TENANT_ID}
              groupItems={groupItems}
              nextItemTitle={nextItem?.title ?? null}
              onVerdictSubmitted={(id, _action) => advanceToNext(id)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── ReviewPage (auth-guarded export) ─────────────────────────────────────────

export function ReviewPage() {
  return (
    <PortalAuthGuard requiredRole="REVIEWER">
      <ReviewPageInner />
    </PortalAuthGuard>
  );
}
